import { HttpClient } from "@utils/index";
import type {
  UpdateDocumentTypeRequest,
  CreateDocumentTypeRequest,
  FlowVersionSummaryResponse,
  FlowVersionSummaryViewModel,
  FlowConfigResponse,
  FlowConfigViewModel,
  CreateFlowConfigRequest,
  ActivateFlowRequest,
  DocumentTypeInfoViewModel,
  DocumentTypeDetailViewModel,
  DocumentTypeInfoModel,
  DocumentTypeDetailModel,
} from "@models/doctype";
import {
  buildDocumentTypesInfoViewModel,
  buildFlowVersionSummariesViewModel,
  buildFlowConfigViewModel,
  buildCreateFlowConfigRequest,
  buildDocumentTypeDetailViewModel,
  buildSingleDocumentTypeDetailViewModel,
} from "@utils/builders/doctype";

const BASE_URL = import.meta.env.VITE_EXTRACTION_API_BASE_URL;

if (!BASE_URL) {
  throw new Error(
    "Missing required environment variable: VITE_EXTRACTION_API_BASE_URL. " +
      "Please check your .env file and ensure the Intelligence Engine API URL is set.",
  );
}

/** Append doctype → mapping-service routing fields to the multipart body.
 *  Only sent when the caller set them, so it works for both create + update. */
const appendMappingFields = (
  formData: FormData,
  req: { mappingEnabled?: boolean; mappingService?: string | null; mappingPluginId?: string | null },
): void => {
  if (req.mappingEnabled !== undefined) {
    formData.append("mapping_enabled", String(req.mappingEnabled));
  }
  if (req.mappingService) {
    formData.append("mapping_service", req.mappingService);
  }
  if (req.mappingPluginId) {
    formData.append("mapping_plugin_id", req.mappingPluginId);
  }
};

class DocTypeApi {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient(`${BASE_URL}`);
  }

  async getDoctypesByProject(): Promise<DocumentTypeInfoViewModel[]> {
    const response = await this.httpClient.makeAuthenticatedRequest<
      DocumentTypeInfoModel[]
    >(`/doctypes`, {
      method: "GET",
    });
    return buildDocumentTypesInfoViewModel(response);
  }

  async getDoctypesDetailByProject(): Promise<DocumentTypeDetailViewModel[]> {
    const response = await this.httpClient.makeAuthenticatedRequest<
      DocumentTypeDetailModel[]
    >(`/doctypes/detail`, {
      method: "GET",
    });

    return buildDocumentTypeDetailViewModel(response);
  }

  /**
   * Create a new document type
   */
  async createDocumentType(
    request: CreateDocumentTypeRequest,
  ): Promise<DocumentTypeDetailViewModel> {
    const formData = new FormData();
    formData.append("doctype_code", request.docTypeCode);
    formData.append("doctype_name", request.docTypeName);
    formData.append("category_id", request.categoryId);
    if (request.description) {
      formData.append("description", request.description);
    }
    formData.append("doctype_status", request.status || "active");
    if (request.logo) {
      formData.append("logo", request.logo, request.logo.name);
    }
    appendMappingFields(formData, request);

    const response =
      await this.httpClient.makeAuthenticatedRequest<DocumentTypeDetailModel>(
        `/doctypes`,
        {
          method: "POST",
          body: formData,
        },
      );

    return buildSingleDocumentTypeDetailViewModel(response);
  }

  /**
   * Update a document type
   */
  async updateDocumentType(
    docTypeId: string,
    request: UpdateDocumentTypeRequest,
  ): Promise<DocumentTypeDetailViewModel> {
    const formData = new FormData();
    if (request.docTypeCode !== undefined) {
      formData.append("doctype_code", request.docTypeCode);
    }
    if (request.docTypeName !== undefined) {
      formData.append("doctype_name", request.docTypeName);
    }
    if (request.description !== undefined) {
      formData.append("description", request.description);
    }
    // category_id is required by the API
    formData.append("category_id", request.categoryId || "");
    if (request.status !== undefined) {
      formData.append("doctype_status", request.status);
    }
    if (request.logo) {
      formData.append("logo", request.logo, request.logo.name);
    }
    appendMappingFields(formData, request);

    const response =
      await this.httpClient.makeAuthenticatedRequest<DocumentTypeDetailModel>(
        `/doctypes/${encodeURIComponent(docTypeId)}`,
        {
          method: "PUT",
          body: formData,
        },
      );

    return buildSingleDocumentTypeDetailViewModel(response);
  }

  // ============================================================================
  // Flow Configuration Operations
  // ============================================================================

  /**
   * Get all flow versions for a document type (summary info only)
   */
  async getFlowVersions(
    docTypeId: string,
  ): Promise<FlowVersionSummaryViewModel[]> {
    const response = await this.httpClient.makeAuthenticatedRequest<
      FlowVersionSummaryResponse[]
    >(`/doctypes/${encodeURIComponent(docTypeId)}/flows`, {
      method: "GET",
    });

    return buildFlowVersionSummariesViewModel(response);
  }

  /**
   * Get detailed flow configuration by version
   */
  async getFlowDetailByVersion(
    docTypeId: string,
    flowBaseId: string,
    version: number,
  ): Promise<FlowConfigViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<FlowConfigResponse>(
        `/doctypes/${encodeURIComponent(docTypeId)}/flows/${encodeURIComponent(flowBaseId)}/versions/${version}`,
        {
          method: "GET",
        },
      );

    return buildFlowConfigViewModel(response);
  }

  /**
   * Create a new flow configuration
   */
  async createFlowConfig(
    docTypeId: string,
    request: CreateFlowConfigRequest,
  ): Promise<FlowConfigViewModel> {
    const apiRequest = buildCreateFlowConfigRequest(request);
    const response =
      await this.httpClient.makeAuthenticatedRequest<FlowConfigResponse>(
        `/doctypes/${encodeURIComponent(docTypeId)}/flows`,
        {
          method: "POST",
          body: JSON.stringify(apiRequest),
        },
      );

    return buildFlowConfigViewModel(response);
  }

  /**
   * Activate a specific flow version for a document type
   */
  async activateFlow(
    docTypeId: string,
    request: ActivateFlowRequest,
  ): Promise<FlowConfigViewModel> {
    const response =
      await this.httpClient.makeAuthenticatedRequest<FlowConfigResponse>(
        `/doctypes/${encodeURIComponent(docTypeId)}/flows/${encodeURIComponent(request.flow_base_id)}/versions/${request.version}`,
        {
          method: "PATCH",
          body: JSON.stringify({ status: "active" }),
        },
      );

    return buildFlowConfigViewModel(response);
  }

  /**
   * Delete a document type
   */
  async deleteDocumentType(docTypeId: string): Promise<void> {
    await this.httpClient.makeAuthenticatedRequest(
      `/doctypes/${encodeURIComponent(docTypeId)}`,
      {
        method: "DELETE",
      },
    );
  }
}

export { DocTypeApi };

export const docTypeApi = new DocTypeApi();
